/**
 * Select — themed dropdown. Same look and Field behavior as Input
 * (label/hint/error, focus ring), for the ~48 ad-hoc <select>s. Pass <option>s
 * as children. A custom caret is drawn so the control matches across browsers.
 */
import { forwardRef, useState, type SelectHTMLAttributes, type ReactNode, type CSSProperties } from 'react';
import { Field } from './Field';
import { controlBase, focusedRing, errorBorder } from './field-styles';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Style for the surrounding Field wrapper (only when a label/hint/error is shown). */
  fieldStyle?: CSSProperties;
}

// Inline SVG caret (encoded), tinted via currentColor isn't possible in
// background-image, so use the muted text color directly.
const caret =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")";

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, fieldStyle, style, onFocus, onBlur, id, children, ...rest },
  ref,
) {
  const [focused, setFocused] = useState(false);
  const control = (
    <select
      ref={ref}
      id={id}
      style={{
        ...controlBase,
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        paddingRight: 32,
        backgroundImage: caret,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 10px center',
        cursor: 'pointer',
        ...(focused ? focusedRing : null),
        ...(error != null ? errorBorder : null),
        ...style,
      }}
      onFocus={(e) => {
        setFocused(true);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        onBlur?.(e);
      }}
      {...rest}
    >
      {children}
    </select>
  );
  if (label == null && hint == null && error == null) return control;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={id} style={fieldStyle}>
      {control}
    </Field>
  );
});
